import { useMemo, useState, useEffect, useCallback } from "react";
import { TextLink, Icon } from "@chaser/components";
import useFetch from "../../hooks/useFetch";
import useShortCode from "../../hooks/useShortCode";
import useModuleInfo from "../../hooks/useModuleInfo";
import SubscriptionService from "../../services/subscription-service";
import AddonBaseModal from "../../components/Addon/BaseModal";
import {
  addonIds,
  addonIdToFeature,
  BillingProvider,
  roles,
} from "../../util/constants";
import RoleGate from "../FeatureGate/RoleGate/index.jsx";
import BillingProviderGate from "../FeatureGate/BillingProviderGate";
import BasicModal from "../BasicModal";

const addonProps = {
  [addonIds.USER]: {
    icon: <Icon name="user" size="24" bordered />,
    title: "Add additional users",
    successToast: "User added successfully",
  },
  [addonIds.ADDITIONAL_USERS]: {
    icon: <Icon name="user" size="24" bordered />,
    title: "Add additional users",
    successToast: "User added successfully",
  },
  [addonIds.TEMPLATE]: {
    icon: <Icon name="statement" size="24" bordered />,
    title: "Add more templates",
    successToast: "Templates added successfully",
  },
  [addonIds.SCHEDULE]: {
    icon: <Icon name="calendar" size="24" bordered />,
    title: "Add more schedules",
    successToast: "Schedule added successfully",
  },
  [addonIds.EMAIL_INTEGRATION]: {
    icon: <Icon name="email-recipient" size="24" bordered />,
    title: "Add an email integration connection",
    successToast: "Email integration added successfully",
  },
  [addonIds.THANKS_FOR_PAYING]: {
    icon: <Icon name="collections" size="24" bordered />,
    title: "Add thanks for paying schedule",
    successToast: "Thanks for paying added successfully",
  },
  [addonIds.CREDIT_CHECK]: {
    icon: <Icon name="search" size="24" bordered />,
    title: "Credit report allowance",
    successToast: "Credit reports added successfully",
    info: "Cost of credits per report will depend on region",
  },
  [addonIds.RECEIVABLES_FORECAST]: {
    icon: <Icon name="forecast" size="24" bordered />,
    title: "Predict your incoming cash to reduce risk",
    successToast: "AR forecast added successfully",
  },
  [addonIds.SMS_CHASING_CREDITS]: {
    icon: <Icon name="sms" size="24" bordered />,
    title: "SMS chasing allowance",
    successToast: "SMS chasing added successfully",
    info: "Cost of credits will depend on region",
  },
  [addonIds.PHONE_CALL_CHASING_CREDITS]: {
    icon: <Icon name="phone" size="24" bordered />,
    title: "Phone call chasing allowance",
    successToast: "Phone call chasing added successfully",
    info: "Cost of credits will depend on region",
  },
  [addonIds.LETTER_CHASING_CREDITS]: {
    icon: <Icon name="email-letter" size="24" bordered />,
    title: "Letter chasing allowance",
    successToast: "Letter chasing added successfully",
    info: "Cost of credits will depend on region",
  },
};

const defaultAddonProps = {
  icon: <Icon name="plus" size="24" bordered />,
  title: "Add-on",
  successToast: "Add-on added successfully",
};

const DEFAULT_ADDON_MAX_QUANTITY = 30;

const getAddonMaxQuantity = (addon) => {
  const quantityStep = addon?.quantityStep || 1;
  const tiers = addon?.tiers || [];

  if (!tiers.length) {
    return DEFAULT_ADDON_MAX_QUANTITY;
  }

  const lastTier = [...tiers]
    .sort((a, b) => a.startingUnit - b.startingUnit)
    .at(-1);
  const highestKnownUnit = lastTier?.endingUnit || lastTier?.startingUnit;

  if (!highestKnownUnit || Number.isNaN(Number(highestKnownUnit))) {
    return DEFAULT_ADDON_MAX_QUANTITY;
  }

  const percentBuffer =
    Math.ceil((highestKnownUnit * 0.1) / quantityStep) * quantityStep;
  const minimumBuffer = quantityStep * 10;
  const buffer = Math.max(percentBuffer, minimumBuffer);

  return highestKnownUnit + buffer;
};

/**
 * Configuration generators for different addon types
 * Each function returns the appropriate configuration based on addon type
 */
const addOnDefaultConfig = {
  /**
   * Configuration for PER_UNIT addons
   * @param {Object} addon - The addon object
   * @param {string} addonId - The addon identifier
   * @returns {Object} Configuration for PER_UNIT addon
   */
  PER_UNIT: (addon, addonId) => ({
    id: addon.id,
    quantity: addon.quantityStep,
    icon: (addonProps[addonId] || defaultAddonProps).icon,
    price: addon.price,
    minQuantity: addon.quantityStep,
    maxQuantity: getAddonMaxQuantity(addon),
    quantityStep: addon.quantityStep,
    currency: addon.currency,
    optionStyleVariant:
      addonId === addonIds.EMAIL_INTEGRATION ? "allowance" : "default",
  }),

  /**
   * Configuration for FLAT_FEE addons
   * These addons can only have a quantity of 1
   * @param {Object} addon - The addon object
   * @param {string} addonId - The addon identifier
   * @returns {Object} Configuration for FLAT_FEE addon with fixed quantity of 1
   */
  FLAT_FEE: (addon, addonId) => ({
    id: addon.id,
    quantity: 1,
    icon: (addonProps[addonId] || defaultAddonProps).icon,
    price: addon.price,
    minQuantity: 1,
    maxQuantity: 1,
    quantityStep: 1,
    currency: addon.currency,
  }),

  /**
   * Configuration for VOLUME addons
   * These addons support tiered pricing
   * @param {Object} addon - The addon object
   * @param {string} addonId - The addon identifier
   * @returns {Object} Configuration for VOLUME addon with tiers
   */
  VOLUME: (addon, addonId) => ({
    id: addon.id,
    quantity: addon.quantityStep,
    icon: (addonProps[addonId] || defaultAddonProps).icon,
    tiers: addon.tiers,
    price: addon.tiers[0].price,
    minQuantity: addon.quantityStep,
    maxQuantity: getAddonMaxQuantity(addon),
    quantityStep: addon.quantityStep,
    currency: addon.currency,
    info: addonProps[addonId]?.info,
  }),
};

const AddAddonModal = ({
  open,
  close,
  onClosed,
  addonId,
  moduleName,
  plan,
  onSuccess,
}) => {
  const shortCode = useShortCode();
  const { refetchSubscriptionData, plan: moduleContextPlan } = useModuleInfo(
    moduleName,
    {
      suspense: false,
    },
  );
  const [isOpen, setIsOpen] = useState(Boolean(open));

  useEffect(() => {
    if (open) {
      setIsOpen(true);
    }
  }, [open]);

  const handleRequestClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleClosed = useCallback(() => {
    if (onClosed) onClosed();
    if (close) close();
  }, [onClosed, close]);

  const { data: addon, loading: fetchingAddon } = useFetch(
    SubscriptionService.getAddon,
    {
      shortCode,
      addonId,
      moduleName,
    },
    {
      suspense: false,
    },
  );

  const resolvedAddonId =
    addonId === addonIds.ADDITIONAL_USERS ? addonIds.USER : addonId;
  const resolvedPlan = {
    ...(moduleContextPlan || {}),
    ...(plan || {}),
    addons: plan?.addons || moduleContextPlan?.addons || {},
    nextBillingDate:
      plan?.nextBillingDate || moduleContextPlan?.nextBillingDate || null,
  };

  const defaultOptions = useMemo(() => {
    if (!addon) return [];
    const formatter = addOnDefaultConfig[addon.type];
    if (!formatter) {
      console.error(`Unknown addon type: ${addon.type}`);
      return [];
    }

    return formatter(addon, resolvedAddonId);
  }, [addon, resolvedAddonId]);

  return (
    <BillingProviderGate
      billingProviders={[BillingProvider.CHARGEBEE]}
      renderGate={
        <BasicModal
          open={isOpen}
          close={handleRequestClose}
          onClosed={handleClosed}
          title="Maximum limit reached"
          description={
            <div>
              Your account does not support add-ons. Please contact{" "}
              <TextLink as="a" href="mailto:support@chaserhq.com">
                support@chaserhq.com
              </TextLink>{" "}
              to upgrade your account and purchase add-ons.
            </div>
          }
        />
      }
    >
      <RoleGate
        roles={[roles.owner, roles.manager, roles.billing]}
        renderGate={
          <BasicModal
            open={isOpen}
            close={handleRequestClose}
            onClosed={handleClosed}
            title="Billing access needs additional permissions"
            description="Please contact your organisation owner to request billing access or ask them to make this update for you."
          />
        }
      >
        <AddonBaseModal
          open={isOpen}
          close={handleRequestClose}
          onClosed={handleClosed}
          moduleName={moduleName}
          defaultOptions={defaultOptions}
          title={(addonProps[resolvedAddonId] || defaultAddonProps).title}
          currentAddon={
            resolvedPlan?.addons?.[addonIdToFeature[resolvedAddonId]]
          }
          nextBillingDate={resolvedPlan?.nextBillingDate}
          successToast={
            (addonProps[resolvedAddonId] || defaultAddonProps).successToast
          }
          onSuccess={async (requestPayload) => {
            refetchSubscriptionData();
            if (onSuccess) await onSuccess(requestPayload);
          }}
          loading={fetchingAddon}
          addon={addon}
        />
      </RoleGate>
    </BillingProviderGate>
  );
};

export default AddAddonModal;
